import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as moment_ from 'moment';
import { AppIntegrationService, AppSettingsService } from 't4-app-integration';
import { ApiService, BookingErrorCode, CacheService, NotificationService, PickupResultCode, ProlongRentalResult, RentalInfo, RentalStatus, RequestDetails, ReturnResultAction, ReturnResultCode, ServiceRequestStatus, ServiceRequestTags, SettingsService, TurboHubEventType, UIService, BookingResult, ResultDetailLevel, ObjectTypeClassInfo, PhotoAngleInfo, SafetyCheckQuestionInfo } from 't4core';
import { SecurityCheckModel } from '../../../lib/Models/SecurityCheckModel';
import { NavigationService } from '../NavigationService/navigation-service.service';
const moment = moment_;

export class ActiveRental {
  public Id: number;
  public Rental: RentalInfo;
  public TimeStamp: Date;
  public PhotoAngles: PhotoAngleInfo[];
  public SafetyCheckQuestions: SafetyCheckQuestionInfo[];
}

@Injectable({
  providedIn: 'root'
})
export class ActiveRentalService {
  private rentals: ActiveRental[] = [];
  private activeRental: ActiveRental;

  public onRentalUpdated: EventEmitter<number> = new EventEmitter<number>();

  public offerReceived: boolean = false;

  public securityCheck = new SecurityCheckModel();
  public securityCheckComment: string = "";

  private fetchSecurityCheck: boolean = true;

  //When the last time Unlock lock action have been called.
  private lastLockUpTime: number = 0;
  private cooldownLock: number = 30;

  private activeNfcScanningRunning = false;


  public secretSafetyCheckFlag: boolean = false;


  

  constructor(private Api: ApiService, private UI: UIService, private appSettings: AppSettingsService, private notifications: NotificationService, private navigationService: NavigationService, private cache: CacheService, private router: Router, private Settings: SettingsService, private appService: AppIntegrationService) {
    // Populate from cache
    var cacheItem = this.cache.get("ARS_ActiveRentals");
    if (cacheItem) {
      this.rentals = cacheItem.data;

      // Try to refresh all from server
      //this.rentals.map(x => this.refreshRental(x.Id));
    }

    //var cacheItem = this.cache.get("ARS_ActiveRentalId");
    //if (cacheItem) {
    //  this.setActiveRental(cacheItem.data);
    //}

    // Subscribe to rental updates
    this.notifications.onEvent.subscribe(x => {
      if (x.Type == TurboHubEventType.Booking) {
        this.refreshRental(parseInt(x.EntityId), true);
      }
    });
  }

  private updateCache() {
    this.cache.put("ARS_ActiveRentals", this.rentals, new Date(new Date().getTime() + (24 * 60 * 60 * 1000)));

    if (this.activeRental)
      this.cache.put("ARS_ActiveRentalId", this.activeRental.Id, new Date(new Date().getTime() + (168 * 60 * 60 * 1000)));
    else
      this.cache.del("ARS_ActiveRentalId");
  }

  // Set currently active rental
  public async setActiveRental(id: number) {
    // Is it already loaded?
    var rental = this.rentals.find(x => x.Id == id);

    // If not, load
    if (!rental) {
      var rentalInfo = await this.Api.get<RentalInfo>("/Rental/GetRentalInfo", { bookingId: id });
      var photoAngleInfo = await this.Api.get<PhotoAngleInfo[]>("Rental/GetPhotoAnglesInfo", { bookingId: id });
      var safetyCheckQuestionsInfo = await this.Api.get<SafetyCheckQuestionInfo[]>("Rental/GetSafetyCheckQuestionsInfo", { bookingId: id });
      rental = {
        Id: id,
        Rental: rentalInfo,
        TimeStamp: new Date(),
        PhotoAngles: photoAngleInfo,
        SafetyCheckQuestions: safetyCheckQuestionsInfo
      };

      this.rentals.push(rental);

      this.updateCache();
    } else {

      await this.refreshRental(id);
    }

    this.activeRental = rental;
    this.updateCache();

    // Set local context
    if (rental.Rental.PickupLocationId) {
      this.appSettings.localLocationId = rental.Rental.PickupLocationId;
    }
  }

  public flagRentalBooking(rentalId: number) {
    if (this.activeRental && this.activeRental.Id == rentalId) {
      this.activeRental.Rental.Flagged = true;
    }
  }

  public async getOverrideTypeClass(typeClassId: number): Promise<number> {

    var res = await this.Api.get<ObjectTypeClassInfo[][]>("Settings/GetAllObjectTypeClasses");
    var typeClasses = res[0];
    const overrrideTypeClass = typeClasses.find(t => t.OverridesClassId == typeClassId);
    if (!overrrideTypeClass) return null;
    return overrrideTypeClass.Id;
  }

  // Update rental from database
  private async refreshRental(id: number, force: boolean = false) {
    var item = this.rentals.find(y => y.Id == id);
    if (item) {

      // If not a forced update, check i newer on server
      if (!force && item.Rental) {
        var hasChanged = await this.Api.get<RentalInfo>("/Data/HasChanged", { type: 'Rental', entityRef: id, version: item.Rental.RowVersion });
        if (!hasChanged) return;
      }

      var source = await this.Api.get<RentalInfo>("/Rental/GetRentalInfo", { bookingId: id });
      if (!item.PhotoAngles) item.PhotoAngles = await this.Api.get<PhotoAngleInfo[]>("Rental/GetPhotoAnglesInfo", { bookingId: id });
      if (!item.SafetyCheckQuestions) item.SafetyCheckQuestions = await this.Api.get<SafetyCheckQuestionInfo[]>("Rental/GetSafetyCheckQuestionsInfo", { bookingId: id });
      if (item.Rental) {
        Object.keys(source).forEach((key) => {
          item.Rental[key] = source[key];
        })
      } else {
        item.Rental = source;
      }

      item.TimeStamp = new Date();
      this.updateCache();
      this.onRentalUpdated.emit(item.Id);
    }
  }

  public async getBooking(): Promise<RentalInfo> {
    if (!this.activeRental) {
      if (this.navigationService.getParam('rentalId')) {
        await this.setActiveRental(parseInt(this.navigationService.getParam('rentalId')));
      } else {
        // Is there anything in the cache?
        var cacheItem = this.cache.get("ARS_ActiveRentalId");
        if (cacheItem) {
          await this.setActiveRental(cacheItem.data);
        }
      }
    }

    return this.activeRental.Rental;
  }

  public async getPhotoAngles(): Promise<PhotoAngleInfo[]> {
    if (!this.activeRental) {
      if (this.navigationService.getParam('rentalId')) {
        await this.setActiveRental(parseInt(this.navigationService.getParam('rentalId')));
      } else {
        // Is there anything in the cache?
        var cacheItem = this.cache.get("ARS_ActiveRentalId");
        if (cacheItem) {
          await this.setActiveRental(cacheItem.data);
        }
      }
    }

    return this.activeRental.PhotoAngles;
  }
  public async getSafetyCheckQuestions(): Promise<SafetyCheckQuestionInfo[]> {
    if (!this.activeRental) {
      if (this.navigationService.getParam('rentalId')) {
        await this.setActiveRental(parseInt(this.navigationService.getParam('rentalId')));
      } else {
        // Is there anything in the cache?
        var cacheItem = this.cache.get("ARS_ActiveRentalId");
        if (cacheItem) {
          await this.setActiveRental(cacheItem.data);
        }
      }
    }

    return this.activeRental.SafetyCheckQuestions;
  }
  // Try to start the rental
  public async startRental(): Promise<PickupResultCode> {

    var pickupModel: PickupModel = {
      RentalId: this.activeRental.Id,
      BypassCardPayment: false,
      Prediclose: false,
      PickupDevice: this.activeRental.Rental.PickupDevice
    }

    var result = await this.Api.post<BookingResult>("/Rental/Disclose", pickupModel);

    if (result.PickupResultCode == PickupResultCode.SuccessWithPayment || result.PickupResultCode == PickupResultCode.SuccessWithoutPayment || result.PickupResultCode == PickupResultCode.InvoicePayment) {
      this.appService.refreshRentals("Started", this.activeRental.Id);
      this.activeRental.Rental.Status = RentalStatus.Active;
      this.offerReceived = result.OfferSent;
      this.updateCache();
    }

    return result.PickupResultCode;
  }



  public async return(): Promise<ReturnResultCode> {
    var returnTime = new Date().toISOString();

    var returnModel: ReturnModel = {
      RentalId: this.activeRental.Id,
      ReturnTime: returnTime,
      ReturnLocationId: this.activeRental.Rental.ReturnLocationId,
      ReceiptAction: ReturnResultAction.Confirm,
      BypassCardPayment: false,
      ExtraReason: "",
      Addons: [],
      Flagged: this.activeRental.Rental.Flagged,
      ReturnDevice: this.activeRental.Rental.ReturnDevice
    };

    var result = await this.Api.post<BookingResult>("/Rental/Return", returnModel);

    if (result.ReturnResultCode == ReturnResultCode.Success || result.ReturnResultCode == ReturnResultCode.InvoicePayment) {
      this.activeRental.Rental.Status = RentalStatus.Returned;
      this.updateCache();
    }

    return result.ReturnResultCode;
  }

  /// Extend the active rental with a number of hours
  public async extend(returnTime: moment.Moment): Promise<ProlongRentalResult> {
    var rental = await this.getBooking();

    var extendToDate = returnTime.clone().toDate().toISOString();
    let ret = await this.Api.get<ProlongRentalResult>("Rental/ExtendToDate", { rentalId: rental.Id, extendTo: extendToDate });

    if (ret == ProlongRentalResult.Success) {
      rental.ReturnTime = returnTime.clone();
    }

    return ret;
  }

  public async getSecurityCheck(event: String): Promise<SecurityCheckModel> {
    var rental = await this.getBooking();
    if (this.fetchSecurityCheck && rental && rental.RentalObject.Id && rental.Id) {
      var token = this.UI.beginLoading("Intellitrailer.RentalService.ReturningObject", "Getting security check", null);
      var data = await this.Api.get<string>("/RentalObject/GetSecurityCheck",
        {
          rentalObjectId: rental.RentalObject.Id,
          rentalId: rental.Id,
          isReturn: event == "Return" ? true : false
        });

      if (data) {
        var securityCheck = new SecurityCheckModel();
        var object = JSON.parse(data);

        securityCheck.Remarks = object.remarks;
        for (let checkPoint of object.securityChecks) {
          if (securityCheck.CheckPoints.find(x => x.Key == checkPoint.key)) {
            securityCheck.CheckPoints[securityCheck.CheckPoints.findIndex(x => x.Key == checkPoint.key)].Ok = checkPoint.ok;
            securityCheck.CheckPoints[securityCheck.CheckPoints.findIndex(x => x.Key == checkPoint.key)].Failed = !checkPoint.ok;
          }
        }
        this.UI.loaderCompleted(token);
        return securityCheck;
      }
    }
    this.fetchSecurityCheck = true;
    this.UI.loaderCompleted(token);
    return null;
  }

  public async saveSecurityCheck(data: SecurityCheckModel, event: String): Promise<boolean> {
    var result = false
    var rental = await this.getBooking();
    if (rental && rental.RentalObject.Id && rental.Id && event != "FaultReport") {
      var token = this.UI.beginLoading("Intellitrailer.RentalService.ReturningObject", "Saving security check", null);

      var dataObject = {
        securityChecks: [],
        remarks: data.Remarks
      };

      for (let checkPoint of data.CheckPoints) {
        var jsonCheckPoint = { key: checkPoint.Key, ok: checkPoint.Ok };
        dataObject.securityChecks.push(jsonCheckPoint);
      }

      var securityCheckInfo = {
        RentalObjectId: rental.RentalObject.Id,
        RentalId: rental.Id,
        JsonData: JSON.stringify(dataObject),
        IsReturn: event == "Return" ? true : false
      };

      result = await this.Api.post<boolean>("/RentalObject/StoreSafetyCheck", securityCheckInfo);

      this.UI.loaderCompleted(token);
    }


    else if (event == "FaultReport") {

      var aggregatedTags = ServiceRequestTags.None;

      if (data.CheckPoints.find(x => x.Key == "NoseWheel").Failed) aggregatedTags |= ServiceRequestTags.SupportWheel;
      if (data.CheckPoints.find(x => x.Key == "SecurityWire").Failed) aggregatedTags |= ServiceRequestTags.Wire;
      if (data.CheckPoints.find(x => x.Key == "Outside").Failed) aggregatedTags |= ServiceRequestTags.SupportWheel;
      if (data.CheckPoints.find(x => x.Key == "Inside").Failed) aggregatedTags |= ServiceRequestTags.SupportWheel;

      var request = await this.Api.post<RequestDetails>("/Service/CreateUserRequest", null, {
        rentalObjectId: rental.RentalObject.Id,
        description: data.Remarks,
        isInsurance: false,
        tags: aggregatedTags,
      });

      if (request && request.Status != ServiceRequestStatus.Invalid) {
        return true;
      }
      else {
        return false;
      }
    }
    return result;
  }

  public fetchSecurity(fetch: boolean) {
    this.fetchSecurityCheck = fetch;
  }

  public async hasLock(): Promise<boolean> {
    var booking = await this.getBooking();
    if (booking && booking.PickupLocation && booking.RentalObject && booking.RentalObject?.Locks?.some((x: any) => x.DeviceType == 0)) {
      return true;
    }
    else {

      if (booking && booking.PickupLocation && booking.PickupLocation.RentalLocationGroupId == 115) {
        return true;
      }

      return false;
    }
  }

  public async hasTag(): Promise<boolean> {
    var booking = await this.getBooking();

    // Does the object has a device of type "NFC" attached to it?
    if (booking && booking.PickupLocation && booking.RentalObject && booking.RentalObject.Locks?.some((x: any) => x.DeviceType == 2)) {
      return true;
    }
    else {
      return false;
    }
  }
  s
  public async allowUnmanned(): Promise<boolean> {
    var lock = await this.hasLock();
    var tag = await this.hasTag();

    var booking = await this.getBooking();
    var oneWayUnmanned = (booking.PickupLocation.EnableOWSelfService && booking.Status < 100) || ((booking.ReturnLocation.EnableOWSelfService || booking.RentalObject.IsSelfServiceEnabled) && booking.Status >= 100); // Self service is allowed from selfservice locations if status is not started, and to self service AND for converted objects if booking is started. 

    // Allow unmanned if wither tag or lcok i present
    return (lock || tag || oneWayUnmanned);
  }

  // Cancel this booking
  public async cancel(): Promise<boolean> {
    var x = await this.UI.prompt("Intellitrailer.MyPage.BookingOverview.CancellationFeedback.Headline", "Cancellation Feedback", "Intellitrailer.MyPage.BookingOverview.ConfirmBookingCancellation.CommentInfo", "Please let us know the reason for your cancellation. Your feedback helps us do better.");
    if (x == null) return false;
    var token = this.UI.beginLoading("Intellitrailer.MyPaage.Overview.CancelBookingMsg", "Canceling booking", null);
    var result = await this.Api.delete<BookingErrorCode>("/Rental/Cancel",
      {
        booking: this.activeRental.Rental.Id,
        comment: x
      }); 
    this.UI.loaderCompleted(token);
    if (result == BookingErrorCode.Valid) {
      this.appService.refreshRentals("Cancellation", this.activeRental.Id);
      this.activeRental.Rental.Status = RentalStatus.Canceled;
      this.UI.alert("Intellitrailer.MyPage.BookingOverview.CancelBookingSuccess.Title", "Cancelled!", "Intellitrailer.MyPage.BookingOverview.CancelBookingSuccess.Message", "The booking has been cancelled.");
      return true;
    }
    else if (result = BookingErrorCode.ChangesNotAllowed) {
      this.UI.alert("ntellitrailer.MyPage.BookingOverview.CancelBookingFailedChangesNotAllowed.Title", "Can’t cancel booking.", "Intellitrailer.MyPage.BookingOverview.CancelBookingFailedChangesNotAllowed.Message", "This booking can’t be cancelled because its start time has passed or changes are no longer allowed at this state.");
      return false;
    }
    else {
      this.UI.alert("Turbo.Core.BookingLink.resendEmailFailed.Headline", "Failed!", "Intellitrailer.MyPage.BookingOverview.CancelBookingFailed.Message", "The booking could not be cancelled. Please try again.");
      return false;
    }
  }

  public storeWithRental(key: string, data: any, expires: Date) {
    this.cache.put(this.activeRental.Id + "_" + key, data, expires);
  }

  public getCached(key: string) {
    return this.cache.get(this.activeRental.Id + "_" + key);
  }


  public UpdateLastUnlock() {
    this.lastLockUpTime = Math.floor(Date.now() / 1000)
  }
  //Check if the last Unlock is older then cooldownLock(30s).
  public async checkAndUpdateLockTimer(): Promise<boolean> {
    let underLimit: boolean = false;
    if ((Math.floor(Date.now() / 1000) - this.lastLockUpTime < this.cooldownLock)) {
      underLimit = true;
    }
    else {
      underLimit = false;
    }
    this.UpdateLastUnlock();
    return underLimit;
  }

  public startActiveNfcScanning() {
    this.activeNfcScanningRunning = true;
  }

  public stopActiveNfcScanning() {
    this.activeNfcScanningRunning = false;
  }

  public async pickupByNfc(regno: string) {
    // Dont run if active scanning is running
    if (this.activeNfcScanningRunning) return;

    // Get relevant bookings
    var rentals = await this.Api.get<RentalInfo[]>("Rental/App/GetMyActiveAndFutureBookings", null, null, null, ResultDetailLevel.Basic);

    // Find the correct booking
    if (rentals.length <= 0) {
      return -1;
    }
    else if (!rentals.some(y => y.RentalObject.Title == regno)) {
      return -2;
    }

    var rental: RentalInfo = rentals.find(y => y.RentalObject.Title == regno);

    // Validate the rental
    // Is the rental ready to be picked up?

    // Already started
    if (rental.Status >= RentalStatus.Active) {
      this.router.navigate(['/Rental/InFlight', { rentalId: rental.Id }]);
      return -3;
    }

    // To early?
    if ((rental.PickupTime.toDate().getTime() - new Date().getTime()) / 1000 / 60 > this.Settings.clientSettings.Bookings.EarlyPickupInMinutes) {
      this.router.navigate(['/Rental/BookingInfo', { rentalId: rental.Id }]);
      return -4;
    }

    // Not at site. We assume you are on site if nfc is scanned

    // Everything is ok! Jump in!

    // Set active booking id
    await this.setActiveRental(rental.Id);

    // Redirect to start booking
    this.navigationService.navigateToView('Rental.NFCLock', { step: 'IsComplete' });
  }

  public async returnByNfc(regno: string) {
    // Dont run if active scanning is running
    if (this.activeNfcScanningRunning) return;

    // Get relevant bookings
    var rentals = await this.Api.get<RentalInfo[]>("Rental/App/GetMyActiveAndFutureBookings", null, null, null, ResultDetailLevel.Basic);

    // Find the correct booking
    if (rentals.length <= 0) {
      return -1;
    }
    else if (!rentals.some(y => y.RentalObject.Title == regno)) {
      return -2;
    }

    var rental: RentalInfo = rentals.find(y => y.RentalObject.Title == regno);


    // Validate the rental
    // Is the rental ready to be returned?


    // Not at site.
    var distance = await this.appService.getDistanceFromUser(rental.PickupLocation.TrailerPointLatitude, rental.PickupLocation.TrailerPointLongitude) * 1000;
    if (!localStorage.getItem('atLocation') && distance > rental.PickupLocation.TrailerPointRadius + 1000) {
      this.router.navigate(['/Rental/InFlight', { rentalId: rental.Id }]);
    }




    // Everything is ok! Jump in!

    // Set active booking id
    await this.setActiveRental(rental.Id);

    // Redirect to start booking
    this.navigationService.navigateToView('Rental.ReturnNFCLock', { step: 'IsComplete' });
  }




  public updateReasonForSwap(comment: string) {
    this.securityCheckComment = comment;
  }

  public getReasonForSwap() {
    return this.securityCheckComment;
  }


}


export interface ReturnModel {
  RentalId: number,
  ReturnTime: string,
  ReturnLocationId: number,
  ReceiptAction: ReturnResultAction,
  Addons: number[],
  ExtraReason: string,
  BypassCardPayment: boolean,
  ReturnDevice: string,
  Flagged: boolean
}

export interface PickupModel {
  RentalId: number,
  BypassCardPayment: boolean,
  Prediclose: boolean,
  PickupDevice: string
}
